import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import '@/styles/SipaStyles.scss';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2pdf from 'html2pdf.js';
import * as Sentry from '@sentry/react';

// ================== Constants / Helpers ==================
const CONTROL_HEIGHT = 38;
const CONTROL_RADIUS = 6;
const CONTROL_FONT_SIZE = '0.85rem';
const DOM_UPDATE_DELAY_MS = 100; // Time to wait for React state updates and browser repaints
const STYLE_APPLICATION_DELAY_MS = 50; // Time to wait for browser to apply CSS changes
const LETTER_SIZE_WIDTH_PX = 816; // 8.5 inches × 96 DPI = 816px
const unify = { height: CONTROL_HEIGHT, lineHeight: `${CONTROL_HEIGHT}px`, borderRadius: CONTROL_RADIUS, fontSize: CONTROL_FONT_SIZE, boxSizing: 'border-box', padding: '0 12px' };

// UUID generator with browser compatibility
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Generic reorder for arrays using dnd-kit ids
const arrayReorderById = (list, activeId, overId, idSelector) => {
  if(activeId === overId) return list;
  const oldIndex = list.findIndex(i=> idSelector(i) === activeId);
  const newIndex = list.findIndex(i=> idSelector(i) === overId);
  if(oldIndex < 0 || newIndex < 0) return list;
  return arrayMove(list, oldIndex, newIndex);
};

// ================== UI Small Components ==================
const WeightBadge = ({ value, editing, onEditClick, onChange, onSave, onCancel, disabled }) => {
  const handleKey = e => { if(e.key==='Enter'){ e.preventDefault(); onSave && onSave(); } if(e.key==='Escape'){ e.preventDefault(); onCancel && onCancel(); } };
  if(editing) return (
    <span style={{display:'flex', alignItems:'center', gap:4}} onClick={e=>e.stopPropagation()}>
      <input type="number" step={0.1} min={0} max={100} value={value} disabled={disabled} onChange={e=>onChange && onChange(e.target.value)} onKeyDown={handleKey} style={{width:56, fontSize:'.55rem', padding:'2px 4px'}} />
      <button type="button" onClick={onSave} style={{border:'none', background:'#059669', color:'#fff', fontSize:'.55rem', padding:'2px 6px', borderRadius:4}}>Save</button>
      <button type="button" onClick={onCancel} style={{border:'none', background:'#999', color:'#fff', fontSize:'.55rem', padding:'2px 6px', borderRadius:4}}>X</button>
    </span>
  );
  return <button type="button" onClick={onEditClick} style={{fontSize:'.55rem', fontWeight:600, background:'#555', color:'#fff', padding:'4px 10px', borderRadius:14, border:'1px solid #444', cursor:'pointer', letterSpacing:.3, minWidth:60}}>{Number(value||0).toFixed(1)}%</button>;
};

const ControlWeightBadge = ({ maxScore, exclude }) => (
  <span title={exclude ? 'Does not contribute to scoring' : 'Max score contribution'}
        style={{fontSize:'.55rem', fontWeight:600, background: exclude ? '#222' : '#eee', color: exclude ? '#fff':'#222', padding:'2px 6px', borderRadius:12, opacity: exclude ? 1 : .75, letterSpacing:.5}}>
    {exclude ? 'NO SCORE' : `${maxScore ?? '-' }%`}
  </span>
);

const Toolbar = ({ children, uniform = true }) => (
  <div style={{border:'1px solid #ddd', background:'#fafafa', borderRadius:8, padding:8, marginBottom:12, display:'flex', gap:8, alignItems:'flex-start', flexWrap:'wrap', alignContent:'flex-start', minHeight: uniform ? 90 : undefined}}>{children}</div>
);

const AutoGrowInput = ({ value, onChange, placeholder, min = 120, max = 600, style, ...rest }) => {
  const width = useMemo(() => {
    const len = value?.length ?? 0;
    return Math.max(min, Math.min(max, (len + 1) * 8));
  }, [value, min, max]);
  return <input {...rest} value={value} placeholder={placeholder} onChange={onChange} style={{ width, flex:'0 0 auto', ...unify, ...style }} />;
};

// ================== Data Constants ==================
const SECTION_CATEGORIES = [
  { id:1, name:'General Appearance', enumName:'GeneralAppearance' },
  { id:2, name:'Operations & Compliance', enumName:'OperationsCompliance' }
];

// ================== Sortable Items ==================
const ListItem = ({ item, selected, highlight, onSelect, onDelete, weightEditor, editing, tempTitle, onTitleChange, onSaveTitle, onCancelEdit, displayTitle }) => {
  const sortableId = item._dndId; // e.g. sec-#, sub-#
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: transition || 'transform 180ms cubic-bezier(.25,.1,.25,1)',
    background: highlight ? 'rgba(230,38,57,0.15)' : selected ? 'var(--primary-color)' : (isDragging ? '#ececec' : ''),
    color: selected ? '#fff' : undefined,
    cursor:'grab', display:'flex', alignItems:'center', gap:8, padding:'4px 8px', minHeight:30, willChange:'transform'
  };
  const handleKey = (e) => { if(e.key==='Enter'){ e.preventDefault(); onSaveTitle(); } if(e.key==='Escape'){ e.preventDefault(); onCancelEdit(); } };
  return (
    <li ref={setNodeRef} style={style} onClick={()=>onSelect(item.id)} {...attributes} {...listeners}>
      <span style={{flex:1, fontSize:'.7rem', userSelect:'none'}}>
        {editing ? (
          <span style={{display:'flex', alignItems:'center', gap:4}} onClick={e=>e.stopPropagation()}>
            <input value={tempTitle} onChange={e=>onTitleChange(e.target.value)} onKeyDown={handleKey} style={{fontSize:'.65rem', padding:'2px 4px', flex:1}} />
            <button type="button" onClick={onSaveTitle} style={{border:'none', background:'#059669', color:'#fff', fontSize:'.55rem', padding:'2px 6px', borderRadius:4}}>Save</button>
            <button type="button" onClick={onCancelEdit} style={{border:'none', background:'#999', color:'#fff', fontSize:'.55rem', padding:'2px 6px', borderRadius:4}}>X</button>
          </span>
        ) : (displayTitle || item.title)}
      </span>
      {weightEditor}
      <button title={editing?"Cancel":"Edit"} onClick={(e)=>{e.stopPropagation(); editing?onCancelEdit():onTitleChange(item.rawTitle||item.title); onSaveTitle && !editing && onSaveTitle('start');}} style={{marginLeft:4, background:selected?'rgba(255,255,255,0.25)':'#ddd', border:'1px solid #888', color:selected?'#fff':'#333', cursor:'pointer', fontSize:'.55rem', lineHeight:1, padding:'2px 6px', borderRadius:4}}>{editing?'⟳':'Edit'}</button>
      <button title="Delete" onClick={(e)=>{e.stopPropagation(); onDelete(item.id);}} style={{marginLeft:6, background:selected?'rgba(255,255,255,0.25)':'#ffe5e5', border:'1px solid #c00', color:'#c00', cursor:'pointer', fontSize:'.55rem', lineHeight:1, padding:'2px 6px', borderRadius:4}}>Del</button>
    </li>
  );
};

// Updated OptionRow: adds hideValue prop (used for new control creation when No Score selected)
const OptionRow = ({ opt, onChange, onToggle, onRemove, hideValue }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  const dragStyle = { transform: transform ? `translate3d(${transform.x}px, ${transform.y}px,0)` : undefined, transition: transition || 'transform 180ms cubic-bezier(.25,.1,.25,1)', background: isDragging ? '#f0f2f5' : '#fff', willChange:'transform' };
  const handleIcon = (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false" style={{display:'block'}}>
      <g stroke="#888" strokeWidth="1.2" strokeLinecap="round">
        <line x1="3" y1="3" x2="11" y2="3"/><line x1="3" y1="7" x2="11" y2="7"/><line x1="3" y1="11" x2="11" y2="11"/>
      </g>
    </svg>
  );
  return (
    <div ref={setNodeRef} style={{...dragStyle, border:'1px solid #e4e4e4', borderRadius: CONTROL_RADIUS, padding:8, display:'flex', flexDirection:'column', gap:4}} {...attributes} {...listeners}>
      <div style={{display:'flex', alignItems:'flex-start', gap:6}}>
        <div style={{cursor:'grab', padding:'4px 2px', display:'flex', alignItems:'center'}} aria-label="Drag option" title="Drag to reorder">{handleIcon}</div>
        <div style={{flex:1}} className="sipa-option-row-grid">
          <input className="sipa-opt-input" placeholder="Text" value={opt.text} onChange={e=>onChange(opt.id,'text',e.target.value)} />
          {!hideValue && <input className="sipa-opt-input" placeholder="Value" value={opt.value} onChange={e=>onChange(opt.id,'value',e.target.value)} />}
          <label className="sipa-opt-enf"><input type="checkbox" checked={opt.enforce} onChange={e=>onToggle(opt.id,e.target.checked)} /> <span>Enf</span></label>
          <button type="button" className="sipa-opt-del" onClick={()=>onRemove(opt.id)}>Del</button>
        </div>
      </div>
      <div style={{fontSize:'.55rem', opacity:.5, textAlign:'right', paddingRight:2}}>Drag to reorder</div>
    </div>
  );
};

const DraggableControlItem = ({ control, selected, onSelect, onDelete, onBeginEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: control._dndId });
  const style = { transform: transform?`translate3d(${transform.x}px,${transform.y}px,0)` : undefined, transition: transition || 'transform 180ms cubic-bezier(.25,.1,.25,1)', background: selected? '#f5f7fa' : (isDragging? '#ececec':'#fff'), color:'#222', cursor:'grab', borderBottom:'1px solid #eee', padding:'6px 8px', fontSize:'.72rem', display:'flex', alignItems:'center', gap:8, willChange:'transform' };
  const smallBtn = { border:'1px solid #ccc', background:'#f8f8f8', color:'#444', fontSize:'.55rem', padding:'3px 6px', cursor:'pointer', borderRadius:4 };
  const dangerBtn = { ...smallBtn, background:'#c6362f', border:'1px solid #b12a23', color:'#fff' };
  return (
    <li ref={setNodeRef} {...attributes} {...listeners} style={style} onClick={()=>onSelect(control.id)}>
      <span style={{flex:1, fontWeight:500}}>{control.label} <span style={{opacity:.55,fontWeight:400}}>({control.type})</span></span>
      <ControlWeightBadge maxScore={control.maxScore} exclude={control.excludeFromScore} />
      <button title="Edit" onClick={(e)=>{e.stopPropagation(); onBeginEdit(control);}} style={smallBtn}>Edit</button>
      <button title="Delete" onClick={(e)=>{e.stopPropagation(); onDelete(control);}} style={dangerBtn}>Del</button>
    </li>
  );
};

// ================== Modal ==================
const ControlEditOptionRow = React.memo(({ opt, onUpdate, onToggle, onRemove, hideValue }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: opt.id });
  const rowStyle = { display:'flex', alignItems:'stretch', gap:12, padding:'10px 12px', background:'#f8f9fa', border:'1px solid #e2e5e7', borderRadius:10, boxShadow:isDragging?'0 2px 6px -1px rgba(0,0,0,.15)':'none', transform: transform?`translate3d(${transform.x}px,${transform.y}px,0)` : undefined, transition: transition || 'transform 180ms cubic-bezier(.25,.1,.25,1)', willChange:'transform' };
  const inputBase = { fontSize:'.65rem', padding:'6px 8px', border:'1px solid #c9c9c9', borderRadius:6, background:'#fff' };
  const deleteBtn = { border:'1px solid #b12a23', background:'#c6362f', color:'#fff', fontSize:'.6rem', padding:'6px 10px', cursor:'pointer', borderRadius:6, alignSelf:'center' };
  const handleIcon = (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false" style={{display:'block'}}>
      <g stroke="#666" strokeWidth="1.2" strokeLinecap="round"><line x1="3" y1="3" x2="11" y2="3"/><line x1="3" y1="7" x2="11" y2="7"/><line x1="3" y1="11" x2="11" y2="11"/></g>
    </svg>
  );
  return (
    <div ref={setNodeRef} style={rowStyle} {...attributes} {...listeners}>
      <span style={{cursor:'grab', userSelect:'none', display:'flex', alignItems:'center'}} title="Drag to reorder">{handleIcon}</span>
      <input style={{...inputBase, flex:3, minWidth:160}} value={opt.text} placeholder="Text" onChange={e=>onUpdate(opt.id,'text',e.target.value)} />
      {!hideValue && (
        <input style={{...inputBase, width:80}} value={opt.value} placeholder="Value" onChange={e=>onUpdate(opt.id,'value',e.target.value)} />
      )}
      <label style={{display:'flex', alignItems:'center', gap:6, fontSize:'.55rem', padding:'0 6px'}}><input type="checkbox" checked={opt.enforce} onChange={e=>onToggle(opt.id,e.target.checked)} /> Enf</label>
      <button type="button" style={deleteBtn} onClick={() => onRemove(opt.id)}>Delete</button>
    </div>
  );
});

const ControlEditModal = ({ open, controlTypes, data, options, onChangeData, onAddOption, onUpdateOption, onToggleOption, onRemoveOption, onReorderOptions, onSave, onCancel }) => {
 const sensors = useSensors(useSensor(PointerSensor,{activationConstraint:{distance:4}}));
 if(!open) return null;
 const handleOptionDragEnd = ({active,over}) => {
    if(!over || active.id===over.id) return;
    const reordered = arrayReorderById(options, active.id, over.id, o=>o.id).map((o,i)=>({...o,sortOrder:i}));
    onReorderOptions(reordered); // parent handles persistence
  };

  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(2px)', zIndex:1300, display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'60px 32px 80px' };
  const modal = { background:'#fff', width:'min(920px, 96vw)', borderRadius:18, boxShadow:'0 10px 34px -4px rgba(0,0,0,.28)', display:'flex', flexDirection:'column', maxHeight:'calc(100vh - 120px)' };
  const header = { padding:'18px 26px 14px', borderBottom:'1px solid #e5e7e9', display:'flex', alignItems:'center', gap:16 };
  const body = { padding:'24px 28px 8px', overflowY:'auto', display:'flex', flexDirection:'column', gap:30 };
  const footer = { padding:'18px 26px 22px', borderTop:'1px solid #e5e7e9', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:16 };
  const labelStyle = { fontSize:'.58rem', fontWeight:600, textTransform:'uppercase', letterSpacing:.7, color:'#444' };
  const inputStyle = { fontSize:'.7rem', padding:'9px 12px', border:'1px solid #c9c9c9', borderRadius:8, background:'#fff', width:'100%', lineHeight:1.2 };
  const sectionGrid = { display:'grid', gap:30, gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))' };
  const btn = { border:'1px solid #c5c7c9', background:'#f4f5f6', color:'#333', fontSize:'.7rem', padding:'9px 18px', cursor:'pointer', borderRadius:8, fontWeight:500 };
  const primaryBtn = { ...btn, background:'#059669', border:'1px solid #04855c', color:'#fff' };
  const optionAddRow = { display:'flex', alignItems:'center', gap:18 };

  // Hide value input if excludeFromScore is checked
  const hideValue = data.excludeFromScore && ['Dropdown','RadioGroup'].includes(data.type);

  return (
    <div style={overlay} onMouseDown={(e)=>{ if(e.target===e.currentTarget) onCancel(); }}>
      <div style={modal} role="dialog" aria-modal="true" aria-label="Edit Control">
        <div style={header}>
          <h2 style={{margin:0, fontSize:'1.05rem', fontWeight:600}}>Edit Control</h2>
          <div style={{marginLeft:'auto'}}><button style={btn} onClick={onCancel}>Close</button></div>
        </div>
        <div style={body}>
          <div style={{display:'flex', flexDirection:'column', gap:10}}>
            <label style={labelStyle}>Label</label>
            <input style={inputStyle} value={data.label} onChange={e=>onChangeData(d=>({...d,label:e.target.value}))} placeholder="Control label" />
          </div>
          <div style={sectionGrid}>
            <div style={{display:'flex', flexDirection:'column', gap:10}}>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={data.type} onChange={e=>onChangeData(d=>({...d,type:e.target.value}))}>{controlTypes.map(t=> <option key={t} value={t}>{t}</option>)}</select>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:10}}>
              <label style={labelStyle}>Max Score</label>
              <input type="number" min={1} max={100} style={inputStyle} value={data.maxScore} disabled={data.excludeFromScore} onChange={e=>onChangeData(d=>({...d,maxScore:parseInt(e.target.value,10)||1}))} />
            </div>
            {['Dropdown','RadioGroup'].includes(data.type) && (
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                <label style={labelStyle}>Allow NA</label>
                <label style={{fontSize:'.66rem', display:'flex', alignItems:'center', gap:8}}><input type="checkbox" checked={data.allowNotApplicable} onChange={e=>onChangeData(d=>({...d,allowNotApplicable:e.target.checked}))} /> Not Applicable permitted</label>
              </div>
            )}
            <div style={{display:'flex', flexDirection:'column', gap:10}}>
              <label style={labelStyle}>Exclude From Score</label>
              <label style={{fontSize:'.66rem', display:'flex', alignItems:'center', gap:8}}><input type="checkbox" checked={data.excludeFromScore} onChange={e=>onChangeData(d=>({...d,excludeFromScore:e.target.checked}))} /> Do not contribute to scoring</label>
            </div>
          </div>
          {['Dropdown','RadioGroup'].includes(data.type) && (
            <div style={{display:'flex', flexDirection:'column', gap:16}}>
              <div style={optionAddRow}>
                <label style={{...labelStyle, margin:0}}>Options</label>
                <button style={primaryBtn} onClick={onAddOption}>Add Option +</button>
              </div>
              {options.length===0 && <div style={{fontSize:'.7rem', opacity:.55}}>No options yet.</div>}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOptionDragEnd}>
                <SortableContext items={options.map(o=>o.id)} strategy={verticalListSortingStrategy}>
                  <div style={{display:'flex', flexDirection:'column', gap:12}}>
                    {options.map(o=> <ControlEditOptionRow key={o.id} opt={o} onUpdate={onUpdateOption} onToggle={onToggleOption} onRemove={onRemoveOption} hideValue={hideValue} />)}
                  </div>
                </SortableContext>
              </DndContext>
              <div style={{fontSize:'.55rem', opacity:.55}}>Drag rows to reorder (top = highest score).</div>
            </div>
          )}
        </div>
        <div style={footer}>
          <div style={{fontSize:'.55rem', opacity:.55}}>Enter = Save • Esc = Cancel • Click backdrop to dismiss</div>
          <div style={{display:'flex', gap:12}}>
            <button style={primaryBtn} onClick={onSave}>Save</button>
            <button style={btn} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================== Main Page Component ==================
const SipaDesignerPage = () => {
  const { apiFetch } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState([]); // each will have _dndId: 'sec-<id>'
  const [newSectionCategoryId, setNewSectionCategoryId] = useState(1);
  const [sectionWeight, setSectionWeight] = useState('');
  const [selectedSection, setSelectedSection] = useState(null);
  const [subSections, setSubSections] = useState([]); // _dndId: 'sub-<id>' (weights now auto-balanced)
  const [selectedSubSection, setSelectedSubSection] = useState(null);
  const [controls, setControls] = useState([]); // _dndId: 'ctrl-<id>'
  const [form, setForm] = useState({ sectionTitle: '', subSectionTitle: '', controlLabel: '', controlType: 'TextMultiLine' });
  const [controlOptions, setControlOptions] = useState([]); // for new control creation
  const [enforceAny, setEnforceAny] = useState(false);
  const [excludeFromScore, setExcludeFromScore] = useState(false); // NEW state for add form
  const [controlMaxScore, setControlMaxScore] = useState(100);
  const [allowNotApplicable, setAllowNotApplicable] = useState(true);
  const [selectedControl, setSelectedControl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewError, setPreviewError] = useState(null);
  const [hoverSubId, setHoverSubId] = useState(null); // for control cross move (sub target highlight)
  const [hoverSectionId,setHoverSectionId] = useState(null); // for subsection cross move (section target highlight)
  const [lastDeleted, setLastDeleted] = useState(null);
  const [previewCollapsed,setPreviewCollapsed] = useState({});
  const [editingSectionWeightId,setEditingSectionWeightId] = useState(null);
  const [tempWeight,setTempWeight] = useState('');
  const [editingSectionId,setEditingSectionId] = useState(null);
  const [editingSubId,setEditingSubId] = useState(null);
  const [tempTitle,setTempTitle] = useState('');
  const [editingControlId,setEditingControlId] = useState(null);
  const [controlEdit,setControlEdit] = useState({ label:'', type:'TextMultiLine', maxScore:100, allowNotApplicable:true, /*enforceAny:false,*/ excludeFromScore:false }); // include excludeFromScore
  const [controlEditOptions,setControlEditOptions] = useState([]);
  const [activeDragId,setActiveDragId] = useState(null); // For DragOverlay

  const controlTypes = ['TextMultiLine','Number','Checkbox','Dropdown','RadioGroup','FileUpload','PhotoCapture','Date'];
  const autoNoScoreTypes = ['TextMultiLine', 'Number', 'FileUpload', 'PhotoCapture', 'Date'];

  // Sensors (single context for sections + subsections + controls movement)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint:{ distance:4 }}));
  const optionSensors = useSensors(useSensor(PointerSensor, { activationConstraint:{ distance:4 }}));

  // Preview
  // Fixed: Process sections sequentially to avoid overwhelming the database connection pool.
  // Previous nested Promise.all() created N×M concurrent connections (sections × subsections),
  // which exhausted the MySQL connection pool and crashed the API.
  const openPreview = async () => {
    setShowPreview(true); setPreviewLoading(true); setPreviewError(null); setPreviewData([]); setPreviewCollapsed({});
    try {
      // Fetch all sections first
      const secRes = await apiFetch('/api/sipa/designer/sections');
      const secs = await secRes.json();
      const sections = Array.isArray(secs) ? secs : [];
      
      const sectionObjects = [];
      
      // Process each section sequentially to limit concurrent database connections
      for (const section of sections) {
        try {
          // Fetch subsections for this section
          const subRes = await apiFetch(`/api/sipa/designer/subsections/${section.id}`);
          const subs = await subRes.json();
          const subsections = Array.isArray(subs) ? subs : [];
          
          const subsWithControls = [];
          
          // Process each subsection sequentially
          for (const sub of subsections) {
            try {
              // Fetch controls for this subsection
              const ctrlRes = await apiFetch(`/api/sipa/designer/controls/${sub.id}`);
              const ctrls = await ctrlRes.json();
              subsWithControls.push({ 
                ...sub, 
                controls: Array.isArray(ctrls) ? ctrls : [] 
              });
            } catch (error) {
                Sentry.captureException(error);
              // Log error but continue processing other subsections
              console.error(`Failed to load controls for subsection ${sub.id}:`, error);
              subsWithControls.push({ ...sub, controls: [] });
            }
          }
          
          sectionObjects.push({ 
            ...section, 
            subSections: subsWithControls 
          });
        } catch (error) {
            Sentry.captureException(error);
          // Log error but continue processing other sections
          console.error(`Failed to load subsections for section ${section.id}:`, error);
          sectionObjects.push({ ...section, subSections: [] });
        }
      }
      
      setPreviewData(sectionObjects.sort((a,b)=> (a.order||0)-(b.order||0)));
    } catch (error) {
        Sentry.captureException(error);
      console.error('Failed to build preview:', error);
      setPreviewError('Failed to build preview');
    }
    finally { setPreviewLoading(false); }
  };
  const closePreview = () => setShowPreview(false);

  // Selection toggles
  const toggleSelectSection = (id) => {
    setEditingSectionId(null);
    if(selectedSection === id){ setSelectedSection(null); setSubSections([]); setSelectedSubSection(null); setControls([]); return; }
    loadSubSections(id);
  };
  const toggleSelectSub = (id) => {
    setEditingSubId(null);
    if(selectedSubSection === id){ setSelectedSubSection(null); setControls([]); return; }
    loadControls(id);
  };
  const toggleSelectControl = (id) => { setSelectedControl(prev=> prev===id? null: id); };

  // Editing Titles
  const extractRawSectionTitle = (s) => s.rawTitle || s.title.replace(/^[^]]+\]\s*/, '');
  const beginEditSection = (s) => { setEditingSectionId(s.id); setTempTitle(extractRawSectionTitle(s)); };
  const cancelEditSection = () => { setEditingSectionId(null); setTempTitle(''); };
  const saveEditSection = async () => {
    const s = sections.find(x=>x.id===editingSectionId); if(!s) return; const clean = (tempTitle||'').trim(); if(!clean){ toast.error('Title required'); return; }
    try {
      await apiFetch(`/api/sipa/designer/sections/${editingSectionId}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ categoryId: s.categoryId, title: clean, active:true }) });
      setSections(prev=> prev.map(p=> p.id===s.id ? { ...p, title:`[${SECTION_CATEGORIES.find(c=>c.id===p.categoryId)?.name||p.categoryId}] ${clean}`, rawTitle:clean } : p));
      toast.success('Section updated');
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        toast.error('Update failed');
    }
    cancelEditSection();
  };
  const beginEditSub = (sub) => { setEditingSubId(sub.id); setTempTitle(sub.title); };
  const cancelEditSub = () => { setEditingSubId(null); setTempTitle(''); };
  const saveEditSub = async () => {
    const sub = subSections.find(x=>x.id===editingSubId); if(!sub) return; const clean = (tempTitle||'').trim(); if(!clean){ toast.error('Title required'); return; }
    try {
      await apiFetch(`/api/sipa/designer/subsections/${editingSubId}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ title: clean, active:true }) });
      setSubSections(prev=> prev.map(p=> p.id===sub.id ? { ...p, title:clean } : p));
      toast.success('SubSection updated');
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        toast.error('Update failed');
    }
    cancelEditSub();
  };

  // Control edit begin
  const beginEditControl = (c) => {
    setEditingControlId(c.id);
    setControlEdit({ label:c.label, type:c.type, maxScore:c.maxScore, allowNotApplicable:c.allowNotApplicable, excludeFromScore: !!c.excludeFromScore });
    setControlEditOptions((c.options||[]).map(o=>({ id:o.id||generateUUID(), text:o.text, value:o.value, enforce:o.enforceActionItem, sortOrder:o.sortOrder, active:o.active!==false })));
  };

  // Cancel control edit - memoized for use in keyboard handler
  const cancelEditControl = useCallback(() => { 
    setEditingControlId(null); 
    setControlEditOptions([]); 
  }, []);

  // Save control edit - memoized for use in keyboard handler
  const saveEditControl = useCallback(async () => {
    const clean = (controlEdit.label||'').trim(); 
    if(!clean){ toast.error('Label required'); return; }
    const payload = { 
      label: clean, 
      type: controlEdit.type, 
      maxScore: controlEdit.maxScore, 
      allowNotApplicable: controlEdit.allowNotApplicable,
      excludeFromScore: controlEdit.excludeFromScore, 
      active:true 
    };
    if(['Dropdown','RadioGroup'].includes(controlEdit.type)) {
      let opts = controlEditOptions.filter(o=>o.text);
      if(controlEdit.excludeFromScore) {
        // Auto-generate unique values for each option
        opts = opts.map((o, i) => ({ ...o, value: o.value || `opt${i+1}` }));
      } else {
        opts = opts.filter(o=>o.value);
      }
      payload.options = opts.map((o,i)=>({ 
        text:o.text, 
        value:o.value, 
        enforceActionItem:o.enforce, 
        sortOrder:i, 
        active:true 
      }));
    }
    try { 
      await apiFetch(`/api/sipa/designer/controls/${editingControlId}`, { 
        method:'PUT', 
        headers:{'Content-Type':'application/json'}, 
        body: JSON.stringify(payload) 
      });
      setControls(prev=> prev.map(p=> p.id===editingControlId ? { 
        ...p, 
        label:clean, 
        type:payload.type, 
        maxScore:payload.maxScore, 
        allowNotApplicable:payload.allowNotApplicable,
        excludeFromScore: payload.excludeFromScore, 
        options: payload.options || p.options 
      } : p));
      toast.success('Control updated');
    } catch (catchError) { 
        Sentry.captureException(catchError);
      toast.error('Update failed'); 
    }
    cancelEditControl();
  }, [controlEdit, controlEditOptions, editingControlId, apiFetch, cancelEditControl]);

  const addEditOption = () => { setControlEditOptions(opts=>[...opts,{ id:generateUUID(), text:'', value:'', enforce:false, sortOrder:opts.length, active:true }]); };
  const updateEditOption = (id,key,val) => { setControlEditOptions(opts=> opts.map(o=> o.id===id?{...o,[key]:val}:o)); };
  const toggleEditOption = (id,val) => updateEditOption(id,'enforce',val);
  const removeEditOption = (id) => { setControlEditOptions(opts=> opts.filter(o=>o.id!==id).map((o,i)=>({...o,sortOrder:i}))); };

  // Immediate option reorder persistence (modal)
  const persistControlOptionOrder = useCallback(async (newList) => {
    setControlEditOptions(newList);
    if(!editingControlId) return; // nothing to persist yet
    try {
      const payload = { 
        label: controlEdit.label || '', 
        type: controlEdit.type, 
        maxScore: controlEdit.maxScore, 
        allowNotApplicable: controlEdit.allowNotApplicable, 
        enforceActionItemOnAnySelection: controlEdit.enforceAny, 
        excludeFromScore: controlEdit.excludeFromScore, 
        active:true, 
        options: newList.filter(o=>o.text && o.value).map((o,i)=> ({ 
          text:o.text, 
          value:o.value, 
          enforceActionItem:o.enforce, 
          sortOrder:i, 
          active:o.active!==false 
        })) 
      };
      await apiFetch(`/api/sipa/designer/controls/${editingControlId}`, { 
        method:'PUT', 
        headers:{'Content-Type':'application/json'}, 
        body: JSON.stringify(payload) 
      });
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* silent */
    }
  }, [editingControlId, controlEdit, apiFetch]);

  // Keyboard shortcuts (modal)
  const controlEditKeyHandler = useCallback((e)=>{ 
    if(!editingControlId) return; 
    if(e.key==='Enter'){ e.preventDefault(); saveEditControl(); } 
    if(e.key==='Escape'){ e.preventDefault(); cancelEditControl(); } 
  },[editingControlId, saveEditControl, cancelEditControl]);

  useEffect(()=>{ 
    if(editingControlId){ 
      window.addEventListener('keydown', controlEditKeyHandler); 
      return ()=> window.removeEventListener('keydown', controlEditKeyHandler); 
    } 
  },[editingControlId, controlEditKeyHandler]);

  // Global keyboard shortcuts (Ctrl+Z for undo)
  const globalKeyHandler = useCallback((e) => {
    // Ctrl+Z or Cmd+Z for undo
    if((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      if(lastDeleted) {
        undoLastDelete();
      }
    }
  }, [lastDeleted]);

  useEffect(() => {
    window.addEventListener('keydown', globalKeyHandler);
    return () => window.removeEventListener('keydown', globalKeyHandler);
  }, [globalKeyHandler]);

  // Loaders
  const loadAllSections = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/sipa/designer/sections');
      const data = await res.json();
      const withCat = (Array.isArray(data)? data: []).map(s => ({...s, _dndId:`sec-${s.id}`, title: `[${SECTION_CATEGORIES.find(c=>c.id===s.categoryId)?.name || s.categoryId}] ${s.title}`}));
      setSections(withCat);
    } finally { setLoading(false); }
  };
  const loadSubSections = async (sectionId) => {
    setSelectedSection(sectionId); setSelectedSubSection(null); setControls([]);
    if(!sectionId){ setSubSections([]); return; }
    const res = await apiFetch(`/api/sipa/designer/subsections/${sectionId}`);
    const data = await res.json();
    setSubSections((Array.isArray(data)? data: []).map(s=>({...s,_dndId:`sub-${s.id}`})));
  };
  const loadControls = async (subSectionId) => {
    setSelectedSubSection(subSectionId);
    if(!subSectionId){ setControls([]); return; }
    const res = await apiFetch(`/api/sipa/designer/controls/${subSectionId}`);
    const data = await res.json();
    setControls((Array.isArray(data)? data: []).map(c=>({...c,_dndId:`ctrl-${c.id}`})));
  };


  // Weight helpers
  const parseDecimal = v => { const n = parseFloat(v); return isNaN(n)?0:n; };
  const totalSectionWeight = sections.reduce((sum,s)=> sum + (s.weight||0),0);
  // Controls scoring total (unchanged)
  const totalControlIncludedScore = controls.reduce((sum,c)=> {
    let excluded = c.excludeFromScore;
    if(editingControlId === c.id) {
      // reflect unsaved change in modal
      excluded = controlEdit.excludeFromScore;
    }
    return excluded ? sum : sum + (c.maxScore ||0);
  },0);
  const canAddSubSection = !!selectedSection;
  const canAddControl = !!selectedSubSection;

  useEffect(() => { setSectionWeight(prev => { const remaining = Math.max(0, 100 - totalSectionWeight); if(remaining <= 0) return ''; if(prev==='') return remaining.toFixed(2).replace(/\.00$/,''); const num=parseFloat(prev); if(isNaN(num)) return remaining.toString(); return Math.min(num, remaining).toString(); }); }, [totalSectionWeight]);

  const handleSectionWeightChange = (e) => { let val=e.target.value; if(val===''){ setSectionWeight(''); return; } let num=parseFloat(val); if(isNaN(num)) num=0; const remaining = Math.max(0, 100 - (totalSectionWeight - num)); if(num>remaining) num=remaining; if(num<0) num=0; setSectionWeight(num.toString()); };

  // Add entities
  const addSection = async () => { const weight = parseDecimal(sectionWeight); if(!form.sectionTitle.trim() || weight<=0 || weight>100 || totalSectionWeight+weight>100.0001) return; const order = sections.length + 1; await apiFetch('/api/sipa/designer/sections',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ categoryId:newSectionCategoryId, title:form.sectionTitle, order, weight, active:true })}); setForm(f=>({...f, sectionTitle:''})); setSectionWeight(''); await loadAllSections(); };
  const addSubSection = async () => { if(!canAddSubSection) return; if(!form.subSectionTitle.trim()) return; const order=subSections.length+1; await apiFetch('/api/sipa/designer/subsections',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ sectionId:selectedSection, title:form.subSectionTitle, order, active:true })}); setForm(f=>({...f, subSectionTitle:''})); await loadSubSections(selectedSection); };
  const addControl = async () => { if(!canAddControl || !form.controlLabel.trim()) return; const maxScore=parseInt(controlMaxScore,10)||100; const allowNA = ['Dropdown','RadioGroup'].includes(form.controlType)?allowNotApplicable:false; const order=controls.length+1; const autoNoScore = autoNoScoreTypes.includes(form.controlType); const effectiveExclude = autoNoScore || excludeFromScore; const effectiveMax = effectiveExclude ? 0 : maxScore; const payload = { subSectionId:selectedSubSection, label:form.controlLabel, order, type:form.controlType, maxScore:effectiveMax, allowNotApplicable:allowNA, active:true, enforceActionItemOnAnySelection:enforceAny, excludeFromScore: effectiveExclude }; if(['Dropdown','RadioGroup'].includes(form.controlType)) { let opts = controlOptions.sort((a,b)=>a.sortOrder-b.sortOrder).filter(o=>o.text); if(effectiveExclude){ opts = opts.map((o,i) => ({ ...o, value: o.value || `opt${i+1}` })); } else { opts = opts.filter(o=>o.value); } payload.options = opts.map((o,i)=>({text:o.text,value:o.value,enforceActionItem:o.enforce,sortOrder:i,active:true})); } await apiFetch('/api/sipa/designer/controls',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload)}); setForm(f=>({...f, controlLabel:''})); setControlOptions([]); setEnforceAny(false); setExcludeFromScore(false); setControlMaxScore(100); setAllowNotApplicable(true); await loadControls(selectedSubSection); };

  // Weight editing save (sections only now)
  const startEditSectionWeight = (id,current) => { setEditingSectionWeightId(id); setTempWeight((current??0).toString()); };
  const cancelEditWeight = () => { setEditingSectionWeightId(null); setTempWeight(''); };
  const saveSectionWeight = async () => { const w=parseFloat(tempWeight); if(isNaN(w)||w<0||w>100){ toast.error('Invalid weight'); return; } const prev=sections; setSections(sections.map(s=> s.id===editingSectionWeightId?{...s, weight:w}:s)); cancelEditWeight(); try { const res = await apiFetch(`/api/sipa/designer/sections/${editingSectionWeightId}/weight`,{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ weight:w })}); if(!res.ok) throw new Error('failed'); toast.success('Section weight updated'); } catch (catchError) { Sentry.captureException(catchError); toast.error('Save failed'); setSections(prev); } };

  // Delete + undo - IMPROVED: Fully restore deleted controls with all properties
  const handleDeleteSection = async (id) => { const section=sections.find(s=>s.id===id); if(!section) return; await apiFetch(`/api/sipa/designer/sections/${id}`,{method:'DELETE'}); setLastDeleted({ type:'section', payload:section }); toast.info(<span>Section deleted. <button style={{marginLeft:8, padding:'2px 8px', background:'#059669', color:'#fff', border:'none', borderRadius:4, cursor:'pointer'}} onClick={()=>undoLastDelete()}>Undo</button></span>, { autoClose:8000 }); await loadAllSections(); if(selectedSection===id){ setSubSections([]); setSelectedSection(null); } };
  const handleDeleteSubSection = async (id) => { const sub=subSections.find(s=>s.id===id); if(!sub) return; await apiFetch(`/api/sipa/designer/subsections/${id}`,{method:'DELETE'}); setLastDeleted({ type:'subSection', payload:sub }); toast.info(<span>SubSection deleted. <button style={{marginLeft:8, padding:'2px 8px', background:'#059669', color:'#fff', border:'none', borderRadius:4, cursor:'pointer'}} onClick={()=>undoLastDelete()}>Undo</button></span>, { autoClose:8000 }); await loadSubSections(selectedSection); };
  const handleDeleteControl = async (ctrl) => { await apiFetch(`/api/sipa/designer/controls/${ctrl.id}`,{method:'DELETE'}); setLastDeleted({ type:'control', payload:ctrl }); toast.info(<span>Control deleted. <button style={{marginLeft:8, padding:'2px 8px', background:'#059669', color:'#fff', border:'none', borderRadius:4, cursor:'pointer'}} onClick={()=>undoLastDelete()}>Undo</button></span>, { autoClose:8000 }); await loadControls(selectedSubSection); };
  
  const undoLastDelete = async () => { 
    if(!lastDeleted) return; 
    const { type, payload } = lastDeleted; 
    try { 
      if(type==='section'){ 
        // Extract raw title (remove category prefix if present)
        const rawTitle = payload.rawTitle || payload.title.replace(/^\[[^\]]+\]\s*/, '');
        await apiFetch('/api/sipa/designer/sections',{
          method:'POST', 
          headers:{'Content-Type':'application/json'}, 
          body: JSON.stringify({ 
            categoryId: payload.categoryId, 
            title: rawTitle, 
            order: payload.order || 0, 
            weight: payload.weight || 0, 
            active: true 
          })
        }); 
        toast.success('Section restored');
        await loadAllSections(); 
      } else if(type==='subSection'){ 
        await apiFetch('/api/sipa/designer/subsections',{
          method:'POST', 
          headers:{'Content-Type':'application/json'}, 
          body: JSON.stringify({ 
            sectionId: selectedSection, 
            title: payload.title, 
            order: payload.order || 0, 
            active: true 
          })
        }); 
        toast.success('SubSection restored');
        await loadSubSections(selectedSection); 
      } else if(type==='control'){ 
        const p = payload;
        const restorePayload = { 
          subSectionId: selectedSubSection, 
          label: p.label, 
          order: p.order || 0, 
          type: p.type, 
          maxScore: p.maxScore || 100, 
          allowNotApplicable: p.allowNotApplicable ?? true, 
          active: true, 
          enforceActionItemOnAnySelection: p.enforceActionItemOnAnySelection || false,
          excludeFromScore: p.excludeFromScore || false
        };
        
        // Restore options for Dropdown/RadioGroup controls
        if(['Dropdown','RadioGroup'].includes(p.type) && p.options && p.options.length > 0) {
          restorePayload.options = p.options
            .sort((a,b)=> a.sortOrder - b.sortOrder)
            .map((opt, index) => ({
              text: opt.text,
              value: opt.value,
              enforceActionItem: opt.enforceActionItem || false,
              sortOrder: index,
              active: true
            }));
        }
        
        await apiFetch('/api/sipa/designer/controls',{
          method:'POST', 
          headers:{'Content-Type':'application/json'}, 
          body: JSON.stringify(restorePayload)
        }); 
        toast.success('Control restored with all options');
        await loadControls(selectedSubSection); 
      } 
    } catch (catchError) { 
        Sentry.captureException(catchError);
      toast.error('Restore failed'); 
    } finally { 
      setLastDeleted(null); 
    } 
  };

  // Global drag handlers for sections + subsections + controls
  const handleGlobalDragMove = (e) => {
    const overId = e.over?.id;
    const activeId = e.active?.id;
    if(activeId?.startsWith('ctrl-')) {
      if(overId && overId.startsWith('sub-')) setHoverSubId(parseInt(overId.replace('sub-',''),10)); else setHoverSubId(null);
    } else if(activeId?.startsWith('sub-')) {
      if(overId && overId.startsWith('sec-')) setHoverSectionId(parseInt(overId.replace('sec-',''),10)); else setHoverSectionId(null);
    }
  };

  const handleGlobalDragEnd = async (e) => {
    const {active, over} = e; setHoverSubId(null); setHoverSectionId(null); setActiveDragId(null);
    if(!over) return; const aId = active.id; const oId = over.id;

    // Section reorder
    if(aId.startsWith('sec-') && oId.startsWith('sec-')) {
      const newList = arrayReorderById(sections, aId, oId, s=>`sec-${s.id}`); if(newList===sections) return; const ordered = newList.map((s,i)=>({...s, order:i+1})); setSections(ordered); apiFetch('/api/sipa/designer/sections/reorder',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({ sectionIds:ordered.map(s=>s.id) })}); return;
    }

    // SubSection dragging
    if(aId.startsWith('sub-')) {
      if(oId.startsWith('sub-')) { // reorder within current section
        const newList = arrayReorderById(subSections, aId, oId, s=>`sub-${s.id}`); if(newList===subSections) return; const ordered = newList.map((s,i)=>({...s, order:i+1})); setSubSections(ordered); apiFetch('/api/sipa/designer/subsections/reorder',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ sectionId:selectedSection, subSectionIds: ordered.map(s=>s.id) })}); return;
      } else if(oId.startsWith('sec-')) { // move to another section
        const subSectionId = parseInt(aId.replace('sub-',''),10); const targetSectionId = parseInt(oId.replace('sec-',''),10);
        if(targetSectionId !== selectedSection) {
          try { await apiFetch('/api/sipa/designer/subsections/move',{ method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ subSectionId, targetSectionId }) }); toast.success('SubSection moved'); }
          catch (catchError) {
              Sentry.captureException(catchError);
              toast.error('Move failed');
          }
          loadSubSections(targetSectionId);
        }
        return;
      }
    }

    // Control dragging
    if(aId.startsWith('ctrl-')) {
      if(oId.startsWith('ctrl-')) { // reorder within subsection
        const newList = arrayReorderById(controls, aId, oId, c=>`ctrl-${c.id}`); if(newList===controls) return; const ordered = newList.map((c,i)=>({...c, order:i+1})); setControls(ordered); apiFetch('/api/sipa/designer/controls/reorder',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ subSectionId:selectedSubSection, controlIds: ordered.map(c=>c.id) })}); return;
      } else if(oId.startsWith('sub-')) { // move to another subsection
        const controlId = parseInt(aId.replace('ctrl-',''),10); const targetSubId = parseInt(oId.replace('sub-',''),10);
        if(targetSubId!==selectedSubSection) { try { await apiFetch('/api/sipa/designer/controls/move',{method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ controlId, targetSubSectionId: targetSubId })}); toast.success('Control moved'); loadControls(selectedSubSection); }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Move failed'); }
        }
        return;
      }
    }
  };

  useEffect(()=>{ loadAllSections(); },[]);

  const buttonStyle = { ...unify, padding:'0 18px', cursor:'pointer', border:'none', background:'#e62639', color:'#fff' };
  const weightColor = used => used>100? '#e00': used===100? '#059669':'#555';

  const previewContentRef = React.useRef(null);

  // PDF download handler
  // This function generates a properly paginated PDF of the SIPA form preview.
  // Key fixes for blank page issue:
  // 1. Expand all collapsed sections before rendering
  // 2. Temporarily modify the preview container to be fully visible (no scroll/flex constraints)
  // 3. Use proper html2canvas settings for full content capture
  // 4. Restore original state after PDF generation
  const handleDownloadPdf = async () => {
    if (!previewContentRef.current) return;
    
    // Prevent PDF generation if data is still loading or if there's no data
    if (previewLoading) {
      alert('Please wait for the preview to finish loading before downloading.');
      return;
    }
    
    if (!previewData || previewData.length === 0) {
      alert('No data available to generate PDF. Please ensure the SIPA form has sections configured.');
      return;
    }
    
    // CRITICAL FIX: Save collapsed state and expand ALL sections
    // Collapsed sections are conditionally rendered (!collapsed && ...) so they're not in DOM
    const originalCollapsedState = { ...previewCollapsed };
    setPreviewCollapsed({}); // Expand all by clearing collapsed state
    
    // Wait for React to re-render with all sections expanded
    // Combined approach: setTimeout for React state update + requestAnimationFrame for browser paint
    // This ensures the DOM is fully updated before PDF generation
    // Increased delay to ensure all content is rendered
    const waitForDomUpdate = () => new Promise(resolve => {
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      }, DOM_UPDATE_DELAY_MS * 2); // Double the delay to ensure content is fully rendered
    });
    
    console.log('PDF Generation: Expanding all sections and waiting for DOM update...');
    await waitForDomUpdate();
    
    const element = previewContentRef.current;
    
    // Verify element has content before proceeding
    if (!element.innerText || element.innerText.trim().length === 0) {
      console.error('PDF Generation: Preview element has no content');
      alert('Unable to generate PDF: Preview content is empty. Please try refreshing the preview.');
      setPreviewCollapsed(originalCollapsedState);
      return;
    }
    
    console.log(`PDF Generation: Element has ${element.children.length} children, scrollHeight: ${element.scrollHeight}px`);
    
    // Store original inline styles so we can restore them
    const originalStyles = {
      overflow: element.style.overflow,
      height: element.style.height,
      maxHeight: element.style.maxHeight,
      flex: element.style.flex,
      position: element.style.position,
      width: element.style.width,
      minWidth: element.style.minWidth
    };
    
    // Temporarily modify styles to make all content visible for html2canvas
    // This removes scroll constraints and flex limitations
    // Set width for A4 page size (210mm = 8.27in, minus margins for proper PDF layout)
    element.style.overflow = 'visible';
    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.flex = 'none';
    element.style.position = 'static';
    element.style.width = '19cm'; // A4 width (21cm) minus margins (1cm left + 1cm right)
    element.style.minWidth = '19cm';
    
    // Wait for browser to apply the style changes
    await new Promise(resolve => setTimeout(resolve, STYLE_APPLICATION_DELAY_MS * 2)); // Double delay for reflow
    
    try {
      // Configure html2pdf with settings optimized for actual content rendering
      // Using A4 paper size (international standard: 210mm × 297mm)
      const opt = {
        margin: [10, 10, 10, 10], // top, right, bottom, left margins in mm
        filename: 'SIPA_Form_Preview.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          logging: true, // Re-enabled to help debug rendering issues
          letterRendering: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          // Use natural element dimensions to capture all content
          width: element.scrollWidth,
          height: element.scrollHeight
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', // A4 paper size (210mm × 297mm)
          orientation: 'portrait',
          compress: true
        },
        // Configure page breaks to avoid splitting content awkwardly
        pagebreak: { 
          mode: ['avoid-all', 'css'],
          before: '.sipa-section-break',
          after: '.sipa-section-break',
          avoid: ['div', 'ul', 'li']
        }
      };
      
      console.log('PDF Generation: Starting html2pdf with options:', opt);
      console.log(`PDF Generation: Element dimensions - width: ${element.scrollWidth}px, height: ${element.scrollHeight}px`);
      
      // Generate PDF directly from the preview element
      await html2pdf().set(opt).from(element).save();
      
      console.log('PDF Generation: PDF saved successfully');
      
    } catch (error) {
        Sentry.captureException(error);
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // Restore original inline styles properly
      // Use removeProperty for null/undefined/empty values to fully restore original state
      Object.keys(originalStyles).forEach(key => {
        const value = originalStyles[key];
        if (value !== undefined && value !== null && value !== '') {
          element.style[key] = value;
        } else {
          element.style.removeProperty(key);
        }
      });
      
      // Restore original collapsed state
      setPreviewCollapsed(originalCollapsedState);
    }
  };

  // Drag overlay renderer
  const renderDragOverlay = () => {
    if(!activeDragId) return null;
    let data; let type;
    if(activeDragId.startsWith('sec-')) { data = sections.find(s=>`sec-${s.id}`===activeDragId); type='sec'; }
    else if(activeDragId.startsWith('sub-')) { data = subSections.find(s=>`sub-${s.id}`===activeDragId); type='sub'; }
    else if(activeDragId.startsWith('ctrl-')) { data = controls.find(c=>`ctrl-${c.id}`===activeDragId); type='ctrl'; }
    if(!data) return null;
    const base = { padding:'6px 10px', borderRadius:6, fontSize:'.7rem', boxShadow:'0 4px 10px rgba(0,0,0,0.25)', background:'#fff', border:'1px solid #d0d0d0', pointerEvents:'none', maxWidth:260, display:'flex', alignItems:'center', gap:8 };
    if(type==='ctrl') return <div style={base}><strong style={{fontWeight:600}}>{data.label}</strong><span style={{opacity:.55}}>({data.type})</span></div>;
    return <div style={{...base, fontWeight:600}}>{data.title}</div>;
  };

  return (
    <div className="sipa-page">
      <div className="sipa-header-row" style={{alignItems:'center'}}>
        <h1 className="section-title">SIPA Designer</h1>
        <div style={{marginLeft:'auto', display:'flex', gap:8}}>
          <button style={buttonStyle} onClick={openPreview}>Preview</button>
          {loading && <span className="sipa-status-text">Loading...</span>}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({active})=> setActiveDragId(active.id)}
        onDragMove={handleGlobalDragMove}
        onDragEnd={handleGlobalDragEnd}
        onDragCancel={()=>{ setHoverSubId(null); setHoverSectionId(null); setActiveDragId(null); }}
      >
        <div className="sipa-grid-panels">
          {/* Sections */}
          <div style={{display:'flex',flexDirection:'column'}}>
            <Toolbar uniform>
              <select style={{flex:'1 1 150px', minWidth:140, ...unify}} value={newSectionCategoryId} onChange={e=>setNewSectionCategoryId(parseInt(e.target.value,10))}>{SECTION_CATEGORIES.map(c=> <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <AutoGrowInput style={{flex:'2 1 200px'}} value={form.sectionTitle} placeholder="New Section" onChange={e=>setForm(f=>({...f,sectionTitle:e.target.value}))} />
              <input type="number" min={0} max={100} step={0.1} style={{width:90, ...unify}} placeholder="Weight %" value={sectionWeight} onChange={handleSectionWeightChange} />
              <button style={buttonStyle} disabled={!form.sectionTitle.trim() || parseDecimal(sectionWeight)<=0} onClick={addSection}>Add</button>
            </Toolbar>
            <div style={{fontSize:'.65rem', margin:'2px 4px 6px', color: weightColor(totalSectionWeight)}}>Sections Weight Used: {totalSectionWeight.toFixed(2)} / 100</div>
            <div className="sipa-panel" style={{flex:1}}>
              <h3>Sections (Global Order / Drop SubSection To Move)</h3>
              <SortableContext items={sections.map(s=>`sec-${s.id}`)} strategy={verticalListSortingStrategy}>
                <ul className="sipa-list-box no-scroll">
                  {sections.map((s,i)=> <ListItem key={s.id} item={s} selected={s.id===selectedSection} highlight={activeDragId?.startsWith('sub-') && hoverSectionId===s.id} onSelect={toggleSelectSection} onDelete={handleDeleteSection} weightEditor={<WeightBadge value={editingSectionWeightId===s.id?tempWeight:s.weight} editing={editingSectionWeightId===s.id} onEditClick={()=>startEditSectionWeight(s.id,s.weight)} onChange={v=>setTempWeight(v)} onSave={saveSectionWeight} onCancel={cancelEditWeight} />} editing={editingSectionId===s.id} tempTitle={editingSectionId===s.id?tempTitle:''} onTitleChange={v=>setTempTitle(v)} onSaveTitle={(flag)=> flag==='start'?beginEditSection(s):saveEditSection()} onCancelEdit={cancelEditSection} displayTitle={`${i+1}. ${s.title}`} />)}
                </ul>
              </SortableContext>
            </div>
          </div>
          {/* SubSections */}
          <div style={{display:'flex',flexDirection:'column'}}>
            <Toolbar uniform>
              <AutoGrowInput disabled={!canAddSubSection} style={{flex:'2 1 220px', opacity: canAddSubSection?1:.5}} value={form.subSectionTitle} placeholder={canAddSubSection?"New SubSection":"Select a Section first"} onChange={e=>setForm(f=>({...f,subSectionTitle:e.target.value}))} />
              <button style={buttonStyle} disabled={!canAddSubSection || !form.subSectionTitle.trim()} onClick={addSubSection}>Add</button>
            </Toolbar>
            <div style={{fontSize:'.65rem', margin:'2px4px6px', color:'#555'}}>
              {subSections.length>0 ? `SubSections: ${subSections.length}` : 'No SubSections yet'}
            </div>
            <div className="sipa-panel" style={{flex:1}}>
              <h3>SubSections (Reorder / Drag onto Section to Move)</h3>
              <SortableContext items={subSections.map(s=>`sub-${s.id}`)} strategy={verticalListSortingStrategy}>
                <ul className="sipa-list-box no-scroll">
                  {subSections.map((s,i)=> <ListItem key={s.id} item={s} selected={s.id===selectedSubSection} highlight={(activeDragId?.startsWith('ctrl-') && hoverSubId===s.id) || (activeDragId?.startsWith('sub-') && s.id!==parseInt(activeDragId.replace('sub-',''),10) && hoverSubId===s.id)} onSelect={toggleSelectSub} onDelete={handleDeleteSubSection} weightEditor={null} editing={editingSubId===s.id} tempTitle={editingSubId===s.id?tempTitle:''} onTitleChange={v=>setTempTitle(v)} onSaveTitle={(flag)=> flag==='start'?beginEditSub(s):saveEditSub()} onCancelEdit={cancelEditSub} displayTitle={`${(sections.sort((a,b)=>a.order-b.order).findIndex(sec=>sec.id===selectedSection)+1) || '?'.toString()}.${i+1} ${s.title}`} />)}
                </ul>
              </SortableContext>
            </div>
          </div>
          {/* Controls */}
          <div style={{display:'flex',flexDirection:'column'}}>
            <Toolbar uniform>
              <AutoGrowInput disabled={!canAddControl} style={{flex:'3 1 220px', opacity:canAddControl?1:.5}} value={form.controlLabel} placeholder={canAddControl?"Control Label":"Select a SubSection first"} onChange={e=>setForm(f=>({...f,controlLabel:e.target.value}))} />
              <select disabled={!canAddControl} style={{flex:'1 1 140px', minWidth:130, ...unify, opacity:canAddControl?1:.5}} value={form.controlType} onChange={e=>{const t=e.target.value; setForm(f=>({...f,controlType:t})); setControlOptions([]); setAllowNotApplicable(true);}}>{controlTypes.map(t=> <option key={t} value={t}>{t}</option>)}</select>
              <input disabled={!canAddControl || autoNoScoreTypes.includes(form.controlType)} type="number" min={1} max={100} style={{width:70, ...unify, opacity:canAddControl?1:.5}} value={autoNoScoreTypes.includes(form.controlType)?0:controlMaxScore} onChange={e=>setControlMaxScore(e.target.value)} title="Max Score" />
              {['Dropdown','RadioGroup'].includes(form.controlType) && (
                <label style={{display:'flex',alignItems:'center',gap:4, ...unify, padding:'0 8px', background:'#fff', border:'1px solid #ccc', opacity:canAddControl?1:.5}}>
                  <input disabled={!canAddControl} type="checkbox" style={{margin:0}} checked={allowNotApplicable} onChange={e=>setAllowNotApplicable(e.target.checked)} /> NA
                </label>
              )}
              {/* Removed AI Any checkbox */}
              <label style={{display:'flex',alignItems:'center',gap:6, ...unify, padding:'0 10px', background:'#fff', border:'1px solid #ccc', opacity:canAddControl?1:.5}}>
                {autoNoScoreTypes.includes(form.controlType) ? (
                  <span style={{fontSize:'.6rem', fontWeight:600}}>No Score</span>
                ) : (
                  <>
                    <input disabled={!canAddControl} type="checkbox" style={{margin:0}} checked={excludeFromScore || autoNoScoreTypes.includes(form.controlType)} onChange={e=>setExcludeFromScore(e.target.checked)} /> No Score
                  </>
                )}
              </label>
              <button style={buttonStyle} disabled={!canAddControl || !form.controlLabel.trim()} onClick={addControl}>Add</button>
            </Toolbar>
            <div style={{fontSize:'.65rem', margin:'2px 4px 6px', color: weightColor(totalControlIncludedScore)}}>
              {`Controls Weight Used: ${totalControlIncludedScore.toFixed(2)}${totalControlIncludedScore<=100 ? ' / 100' : ''}`}
            </div>
            {canAddControl && ['Dropdown','RadioGroup'].includes(form.controlType) && (
              <div style={{border:'1px solid #ddd', background:'#fafafa', borderRadius:8, padding:12, marginBottom:12}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                  <strong style={{fontSize:'.8rem'}}>Options (order = score gradient)</strong>
                  <button disabled={!canAddControl} type="button" style={{...buttonStyle, padding:'0 12px', opacity:canAddControl?1:.6}} onClick={()=> setControlOptions(o=>[...o,{ id:generateUUID(), text:'', value:'', enforce:false, sortOrder:o.length, active:true }])}>+</button>
                </div>
                <DndContext sensors={optionSensors} collisionDetection={closestCenter} onDragEnd={({active,over})=>{ if(!over||active.id===over.id) return; setControlOptions(prev=> arrayReorderById(prev, active.id, over.id, o=>o.id).map((o,i)=>({...o, sortOrder:i}))); }}>
                  <SortableContext items={controlOptions.map(o=>o.id)} strategy={verticalListSortingStrategy}>
                    <div style={{display:'flex', flexDirection:'column', gap:10}}>
                      {controlOptions.map(o=> <OptionRow key={o.id} opt={o} hideValue={(excludeFromScore || autoNoScoreTypes.includes(form.controlType)) && ['Dropdown','RadioGroup'].includes(form.controlType)} onChange={(id,key,val)=> setControlOptions(prev=> prev.map(p=> p.id===id?{...p,[key]:val}:p))} onToggle={(id,val)=> setControlOptions(prev=> prev.map(p=> p.id===id?{...p,enforce:val}:p))} onRemove={(id)=> setControlOptions(prev=> prev.filter(p=>p.id!==id)) } />)}
                    </div>
                  </SortableContext>
                </DndContext>
                {controlOptions.length===0 && <div style={{fontSize:'.65rem', opacity:.6, marginTop:6}}>No options yet. Click + to add.</div>}
              </div>
            )}
            <div className="sipa-panel" style={{flex:1}}>
              <h3>Controls (Reorder / Drag onto SubSection to Move)</h3>
              {canAddControl && (
                <SortableContext items={controls.map(c=>`ctrl-${c.id}`)} strategy={verticalListSortingStrategy}>
                  <ul className="sipa-list-box no-scroll">
                    {controls.map(c=> (
                      <DraggableControlItem
                        key={c.id}
                        control={c}
                        selected={c.id===selectedControl}
                        onSelect={toggleSelectControl}
                        onDelete={handleDeleteControl}
                        onBeginEdit={beginEditControl}
                      />
                    ))}
                  </ul>
                </SortableContext>
              )}
              {!canAddControl && <div style={{fontSize:'.65rem', opacity:.6}}>Select a SubSection to view / move controls.</div>}
              {canAddControl && subSections.filter(s=>s.id!==selectedSubSection).length>0 && <div style={{marginTop:10, fontSize:'.6rem', opacity:.6}}>Drag a control over another subsection (middle column) to move.</div>}
            </div>
          </div>
        </div>
        <DragOverlay dropAnimation={null}>{renderDragOverlay()}</DragOverlay>
      </DndContext>

      {showPreview && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
          <div style={{width:'90vw', height:'90vh', background:'#fff', borderRadius:12, display:'flex', flexDirection:'column', boxShadow:'04px18px rgba(0,0,0,0.2)'}}>
            <div style={{padding:'12px18px', borderBottom:'1px solid #ddd', display:'flex', alignItems:'center', gap:12}}>
              <h2 style={{margin:0, fontSize:'1.1rem'}}>SIPA Form Preview</h2>
              {previewLoading && <span style={{fontSize:'.75rem', opacity:.7}}>Building...</span>}
              {previewError && <span style={{fontSize:'.75rem', color:'#e00'}}>{previewError}</span>}
              <div style={{marginLeft:'auto', display:'flex', gap:8}}>
                <button style={{...unify, background:'#555', color:'#fff', cursor:'pointer', border:'none'}} onClick={closePreview}>Close</button>
                <button 
                  style={{...unify, background: previewLoading ? '#ccc' : '#059669', color:'#fff', cursor: previewLoading ? 'not-allowed' : 'pointer', border:'none'}} 
                  onClick={handleDownloadPdf}
                  disabled={previewLoading}
                  title={previewLoading ? 'Please wait for preview to finish loading' : 'Download PDF'}
                >
                  Download PDF
                </button>
              </div>
            </div>
            <div ref={previewContentRef} style={{flex:1, overflow:'auto', padding:'18px'}}>
              {!previewLoading && previewData.map((sec,idx) => {
 const collapsed = previewCollapsed[sec.id];
 return (
 <div key={sec.id} style={{marginBottom: 16, border:'1px solid #d4d4d4', borderRadius:10, overflow:'hidden'}}>
 <div onClick={()=> setPreviewCollapsed(c=>({...c,[sec.id]:!c[sec.id]}))} style={{background:'#e62639', color:'#fff', padding:'10px 16px', display:'flex', alignItems:'center', gap:12, cursor:'pointer', fontSize:'.9rem', fontWeight:600}}>
 <span style={{flex:1}}>{idx+1}. {sec.title}</span>
 <span style={{fontSize:'.75rem', fontWeight:400, opacity:.85}}>{collapsed? 'Expand':'Collapse'}</span>
 <span style={{fontSize:'.85rem', fontWeight:700}}>{collapsed? '▸':'▾'}</span>
 </div>
 {!collapsed && (
 <div style={{padding:'14px 16px', background:'#fafafa'}}>
 {sec.subSections.length===0 && <div style={{fontSize:'.7rem', opacity:.6, marginBottom:12}}>No subsections</div>}
 {[...sec.subSections].sort((a,b)=>a.order-b.order).map((sub, sIdx) => (
 <div key={sub.id} style={{marginBottom: 18}}>
 <div style={{fontWeight:600, fontSize:'.8rem', marginBottom: 6}}>{`${idx+1}.${sIdx+1} ${sub.title}`}</div>
 {sub.controls.length===0 && <div style={{fontSize:'.65rem', opacity:.55, marginBottom:6}}>No controls</div>}
 <div style={{display:'flex', flexDirection:'row', flexWrap:'wrap', gap:12}}>
 {sub.controls.sort((a,b)=>a.order-b.order).map(ctrl => (
 <div key={ctrl.id} style={{border:'1px solid #ddd', borderRadius:6, padding:'8px 10px', background:'#fff', display:'flex', flexDirection:'column', gap:6, minWidth:200, maxWidth:280, flex:'1 1 200px'}}>
 <div style={{fontWeight:600, fontSize:'.75rem'}}>{ctrl.label} <span style={{fontWeight:400, opacity:.55}}>({ctrl.type})</span></div>
 {ctrl.options && ctrl.options.length>0 && (
 <ul style={{margin:0, paddingLeft:16, fontSize:'.62rem', display:'flex', flexDirection:'column', gap:2}}>
 {ctrl.options.sort((a,b)=>a.sortOrder-b.sortOrder).map(o=> <li key={o.id || o.value}>{o.text} <span style={{opacity:.5}}>[{o.value}]</span>{o.enforceActionItem && <span style={{color:'#e62639', marginLeft:6}}>Enforce</span>}</li>)}
 </ul>
 )}
 </div>
 ))}
 </div>
 </div>
 ))}

 </div>
 )}
 </div>
 );
 })}
              {!previewLoading && previewData.length===0 && <div style={{fontSize:'.75rem', opacity:.6}}>No sections to display.</div>}
            </div>
          </div>
        </div>
      )}

      {editingControlId && (
        <ControlEditModal
          open={!!editingControlId}
          controlTypes={controlTypes}
          data={controlEdit}
          options={controlEditOptions}
          onChangeData={setControlEdit}
          onAddOption={addEditOption}
          onUpdateOption={updateEditOption}
          onToggleOption={toggleEditOption}
          onRemoveOption={removeEditOption}
          onReorderOptions={persistControlOptionOrder}
          onSave={saveEditControl}
          onCancel={cancelEditControl}
        />
      )}

      {/* Toast notifications container */}
</div>
  );
};

export default SipaDesignerPage;